import { useMemo, useState } from 'react';
import { ChevronRight, Plus, Search } from 'lucide-react';
import clsx from 'clsx';
import type { JamesThread } from '../../api/james';
import { formatTime, formatWeekday, formatDayMonth, formatMonthYear } from '../../utils/dateFormat';
import { cleanThreadTitle } from './threadTitle';

interface JamesThreadListProps {
  threads: JamesThread[];
  activeThreadId: string | null;
  onThreadSelect: (threadId: string | null) => void;
  onNewThread: () => void;
  isLoading?: boolean;
}

/** "Today", "Yesterday", "This week", then one bucket per month ("September 2026"). */
type Bucket = string;

const PROBE_QUESTION = 'what companies are in the crm?';

/**
 * Test threads, hidden by default (display only, nothing is deleted): titles
 * like "Test" / "testing 2", and repeats of the stock probe question beyond its
 * newest copy. Threads arrive newest first.
 */
function splitTestThreads(threads: JamesThread[]): { real: JamesThread[]; tests: JamesThread[] } {
  const real: JamesThread[] = [];
  const tests: JamesThread[] = [];
  let probeSeen = false;
  for (const t of threads) {
    const title = stripMarkdown(t.title || '').trim().toLowerCase();
    if (/^test(ing)?\b/.test(title)) {
      tests.push(t);
    } else if (title === PROBE_QUESTION) {
      if (probeSeen) tests.push(t);
      else real.push(t);
      probeSeen = true;
    } else {
      real.push(t);
    }
  }
  return { real, tests };
}

export function JamesThreadList({
  threads,
  activeThreadId,
  onThreadSelect,
  onNewThread,
  isLoading,
}: JamesThreadListProps) {
  const [query, setQuery] = useState('');
  const [showTests, setShowTests] = useState(false);

  const { real, tests } = useMemo(() => splitTestThreads(threads), [threads]);
  const shown = showTests ? threads : real;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return shown;
    return shown.filter((t) => {
      const title = (t.title || '').toLowerCase();
      const preview = stripMarkdown(t.messages?.[0]?.content || '').toLowerCase();
      return title.includes(q) || preview.includes(q);
    });
  }, [shown, query]);

  const grouped = useMemo(() => groupThreads(filtered), [filtered]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface-secondary">
      <div className="flex-shrink-0 px-4 pt-4 pb-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="eyebrow mb-1">James</p>
            <div className="flex items-center gap-2">
            <h2 className="font-display text-heading-sm tracking-tight text-text-primary">
              Conversations
            </h2>
            {shown.length > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-surface-tertiary px-1.5 text-body-xs font-medium tabular-nums text-text-tertiary">
                {shown.length}
              </span>
            )}
            </div>
          </div>
          <button
            onClick={onNewThread}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-surface-inverse text-text-inverse transition-transform hover:scale-105"
            title="New conversation"
            aria-label="New conversation"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>

        {threads.length > 0 && (
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search conversations"
              className="block w-full rounded-lg border border-border-light bg-surface-primary py-1.5 pl-8 pr-2 text-body-xs text-text-primary placeholder-text-muted transition-colors focus:border-text-primary focus:bg-surface-primary focus:outline-none"
            />
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-text-primary" />
          </div>
        ) : threads.length === 0 ? (
          <div className="mx-3 mt-2 rounded-2xl border border-dashed border-border-light bg-surface-primary px-4 py-8 text-center">
            <img
              src="/sequesto-icon.png"
              alt="Sequesto"
              className="mx-auto h-10 w-10 select-none rounded-xl"
              draggable={false}
            />
            <p className="mt-3 text-body-sm font-medium text-text-primary">No conversations yet</p>
            <p className="mt-1 text-body-xs text-text-tertiary">
              Ask James about companies, deals, or contacts to get started.
            </p>
            <button
              onClick={onNewThread}
              className="btn btn-primary mt-4 h-8 px-3 text-body-xs"
            >
              Start a conversation
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mx-3 mt-4 rounded-xl border border-dashed border-border-light px-4 py-6 text-center">
            <p className="text-body-xs text-text-tertiary">No matches for &ldquo;{query}&rdquo;</p>
          </div>
        ) : (
          <div className="pb-4">
            {grouped.map(([bucket, items], idx) => {
              return (
                <div key={bucket} className={clsx(idx > 0 && 'mt-4')}>
                  <div className="flex items-center gap-2 px-4 py-1">
                    <span className="eyebrow">{bucket}</span>
                    <span className="h-px flex-1 bg-border-light" />
                  </div>
                  <div className="mt-1 space-y-0.5 px-2">
                    {items.map((thread) => (
                      <ThreadItem
                        key={thread.id}
                        thread={thread}
                        isActive={activeThreadId === thread.id}
                        onClick={() => onThreadSelect(thread.id)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!isLoading && tests.length > 0 && (
          <button
            type="button"
            onClick={() => setShowTests((v) => !v)}
            className="mx-4 mb-4 text-body-xs text-text-tertiary hover:text-text-primary"
          >
            {showTests
              ? `Hide ${tests.length} test thread${tests.length === 1 ? '' : 's'}`
              : `${tests.length} test thread${tests.length === 1 ? '' : 's'} hidden · show`}
          </button>
        )}
      </div>
    </div>
  );
}

interface ThreadItemProps {
  thread: JamesThread;
  isActive: boolean;
  onClick: () => void;
}

function ThreadItem({ thread, isActive, onClick }: ThreadItemProps) {
  const title = stripMarkdown(thread.title || '') || 'New conversation';
  const lastMessage = thread.messages?.[0];
  const preview = previewText(lastMessage?.content ?? '');

  return (
    <button
      onClick={onClick}
      className={clsx(
        'group block w-full rounded-lg border px-3 py-2 text-left transition-colors',
        // Selection is a white hairline card on the warm list, no side bar
        isActive
          ? 'border-border-light bg-surface-primary shadow-subtle'
          : 'border-transparent hover:bg-surface-tertiary/60'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        {/* Two lines, so a long question still reads as itself */}
        <p
          className={clsx(
            'min-w-0 line-clamp-2 break-words text-body-sm text-text-primary',
            isActive && 'font-medium'
          )}
          title={title}
        >
          {title}
        </p>
        <ChevronRight
          aria-hidden
          className={clsx(
            'mt-1 h-3 w-3 flex-shrink-0 -translate-x-1 text-text-muted opacity-0 transition-all duration-200',
            !isActive && 'group-hover:translate-x-0 group-hover:opacity-100'
          )}
        />
      </div>
      <div className="mt-0.5 flex items-center gap-2">
        {preview && (
          <p className="min-w-0 flex-1 truncate text-body-xs text-text-tertiary" title={preview}>
            {preview}
          </p>
        )}
        <span className="flex-shrink-0 text-body-xs tabular-nums text-text-muted">
          {formatDate(thread.updatedAt)}
        </span>
      </div>
    </button>
  );
}

/**
 * Titles and previews are one line of plain text: no **bold**, `code`,
 * # headings, > quotes (also without a space, ">Most bid stacks…"), list
 * markers, links or tables, and no leading bar, rule or bullet glyph.
 */
function stripMarkdown(text: string): string {
  return cleanThreadTitle(text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}(?:>\s*)+/gm, '')
    .replace(/^\s{0,3}(#{1,6}|[-*+]|\d+\.)\s+/gm, '')
    .replace(/(\*\*|__|\*|_|~~)(?=\S)([\s\S]*?\S)\1/g, '$2')
    .replace(/[|]/g, ' ')
    .replace(/^\s*:?-{3,}:?\s*$/gm, '')
    .replace(/\s+/g, ' '));
}

/**
 * One line of preview: sentences that ran together ("rules.Hmm") get their
 * space back, and a long preview is cut at a word, never mid-word.
 */
function previewText(content: string, max = 90): string {
  const text = stripMarkdown(content).replace(/([a-z][.!?])(?=[A-Z][a-z])/g, '$1 ');
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,.;:–-]+$/, '')}…`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfWeek(d: Date): Date {
  const date = startOfDay(d);
  const offset = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - offset);
  return date;
}

function dayDiff(now: Date, then: Date): number {
  return Math.round((startOfDay(now).getTime() - startOfDay(then).getTime()) / 86_400_000);
}

function bucketFor(dateString: string): Bucket {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = dayDiff(now, date);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (startOfDay(date) >= startOfWeek(now)) return 'This week';
  return formatMonthYear(date);
}

/** Buckets in the order threads arrive (newest first), so months fall in date order. */
function groupThreads(threads: JamesThread[]): [Bucket, JamesThread[]][] {
  const sorted = [...threads].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const groups = new Map<Bucket, JamesThread[]>();
  for (const thread of sorted) {
    const key = bucketFor(thread.updatedAt);
    groups.set(key, [...(groups.get(key) ?? []), thread]);
  }
  return [...groups.entries()];
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = dayDiff(now, date);

  if (diffDays === 0) return formatTime(date);
  if (diffDays === 1) return 'Yesterday';
  if (startOfDay(date) >= startOfWeek(now)) return formatWeekday(date);
  return formatDayMonth(date);
}
